"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import { KeyStream } from "@/components/tools/ciphers-polygraphic/key-stream";
import { SquareOptions } from "@/components/tools/ciphers-polygraphic/square-options";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { groupLetters } from "@/lib/tool-kit/ciphers/alphabet";
import {
  type AdfgvxResult,
  adfgvxTransform,
  transpositionUsable,
} from "@/lib/tool-kit/ciphers-polygraphic/adfgvx";
import type {
  GridAlphabet,
  SquareFill,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

const PAIR_LIMIT = 24;

/** English strings. A translated content file overrides these through `ui`. */
export const ADFGVX_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Attack at 1200am",
  keyword: "Square keyword",
  keywordSample: "NA1C3H8TB2OME5WRPD4F6G7I9J0KLQSUVXYZ",
  keywordHint:
    "Its characters fill the square first, each used once; the rest of the alphabet (and the digits) follow. Type all 25 or 36 characters to set the square exactly.",
  kind: "The square",
  adfgxMerge: "ADFGX — 5 × 5, I and J share a cell",
  adfgxDropQ: "ADFGX — 5 × 5, leave out Q",
  adfgvx: "ADFGVX — 6 × 6, letters and digits",
  fill: "Write the keyword into the square",
  fillHint:
    "Along the rows is the usual way. A real square was an arbitrary mixed alphabet, so a full 25- or 36-character keyword fixes it whichever way you choose.",
  fillRows: "Along the rows",
  fillColumns: "Down the columns",
  fillSpiral: "In a clockwise spiral",
  transposition: "Transposition keyword",
  transpositionSample: "PRIVACY",
  transpositionHint:
    "Sets the order of the columns in the second stage. Equal letters are taken left to right. Use at least two letters or digits.",
  group: "Cipher text in groups of five",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  letterNote:
    "Only letters (and digits, in the 6 × 6 square) are enciphered. Spaces and punctuation are dropped, and the result is in capitals. The cipher text uses only the letters A, D, F, G, X (and V).",
  ijNote: "I and J share a cell, so every J comes back as I.",
  shortKey:
    "Enter a transposition keyword with at least two letters or digits to see the result.",
  droppedQ:
    "one: {count} Q was left out — this square has no Q.|other: {count} Qs were left out — this square has no Q.",
  invalid:
    "one: {count} character is not one of the letters {labels} and was ignored.|other: {count} characters are not among the letters {labels} and were ignored.",
  unpaired:
    "The transposed letters have an odd count, so the last one ({letter}) has no partner and was not decoded. Check the cipher text and the transposition keyword.",
  working: "Both stages",
  stageOne: "Stage 1 — the Polybius square",
  stageOneRule:
    "Each plain character becomes two letters: the label of its row, then the label of its column.",
  squareLabel: "Key square",
  stageTwo: "Stage 2 — the columnar transposition",
  stageTwoRuleEncode:
    "Write the pair letters in rows under the keyword. Number the columns in the alphabetical order of the keyword's letters (equal letters left to right) and read the columns off in that order, top to bottom.",
  stageTwoRuleDecode:
    "Decoding starts here. The cipher text is cut into columns — the longer ones first, in the order of the numbers — and written back into the grid. Reading the grid by rows gives the pair letters, and each pair names a plain character in the square.",
  gridLabel: "Transposition grid",
  gridHint:
    "The first row is the order the columns are read in. The last row may be short; it is not padded.",
  plainRow: "Plain",
  pairRow: "Pair",
  pairsLabel: "Characters and their pairs",
  pairsLimit:
    "one: Showing the first pair.|other: Showing the first {count} pairs.",
  fractionated: "Pair letters in order",
} as const;

type Strings = typeof ADFGVX_CIPHER_STRINGS;

function GridView({
  result,
  strings,
}: {
  result: AdfgvxResult;
  strings: Strings;
}) {
  const grid = result.grid;
  if (!grid) {
    return null;
  }
  const width = grid.key.length;
  const cells: (string | null)[] = [
    ...grid.ranks.map((rank) => String(rank + 1)),
    ...grid.rows.flat(),
  ];
  const tone = (index: number): GridTone | undefined =>
    index < width ? "muted" : undefined;
  return (
    <div className="space-y-2">
      <CipherGrid
        cells={cells}
        columnHeaders={grid.key}
        columns={width}
        label={strings.gridLabel}
        tone={tone}
      />
      <p className={HINT}>{strings.gridHint}</p>
    </div>
  );
}

function Working({
  result,
  encoding,
  strings,
}: {
  result: AdfgvxResult;
  encoding: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const labels = [...result.labels];
  const shown = result.pairs.slice(0, PAIR_LIMIT);
  const stageOne = (
    <section className="space-y-3" key="one">
      <p className={LABEL}>{strings.stageOne}</p>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.stageOneRule}
      </p>
      <CipherGrid
        cells={[...result.square]}
        columnHeaders={labels}
        columns={result.size}
        label={strings.squareLabel}
        rowHeaders={labels}
      />
      {shown.length > 0 ? (
        <KeyStream
          note={
            result.pairs.length > PAIR_LIMIT
              ? tPlural(strings.pairsLimit, locale, PAIR_LIMIT, {
                  count: fmt(PAIR_LIMIT),
                })
              : undefined
          }
          rows={[
            { label: strings.plainRow, letters: shown.map((p) => p.plain) },
            { label: strings.pairRow, letters: shown.map((p) => p.pair) },
          ]}
          shaded={0}
          title={strings.pairsLabel}
        />
      ) : null}
    </section>
  );
  const stageTwo = (
    <section className="space-y-3" key="two">
      <p className={LABEL}>{strings.stageTwo}</p>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.stageTwoRuleEncode : strings.stageTwoRuleDecode}
      </p>
      <GridView result={result} strings={strings} />
      <p className={HINT}>
        {strings.fractionated}:{" "}
        <span className="break-all font-mono text-foreground">
          {result.fractionated}
        </span>
      </p>
    </section>
  );
  return <>{encoding ? [stageOne, stageTwo] : [stageTwo, stageOne]}</>;
}

export default function AdfgvxCipherWidget() {
  const strings = useToolUi(ADFGVX_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [kind, setKind] = useState<GridAlphabet>("alnum");
  const [fill, setFill] = useState<SquareFill>("rows");
  const [transposition, setTransposition] = useState<string>(
    strings.transpositionSample
  );
  const [group, setGroup] = useState(true);

  const encoding = mode === "encode";
  const usable = transpositionUsable(transposition);
  const result = adfgvxTransform(
    input,
    { keyword, kind, fill, transposition },
    mode
  );
  const output =
    group && encoding ? groupLetters(result.output, 5) : result.output;

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.keywordHint}
          id="ad-keyword"
          label={strings.keyword}
          mono
          onChange={setKeyword}
          value={keyword}
        />
        <TextField
          hint={strings.transpositionHint}
          id="ad-transposition"
          label={strings.transposition}
          mono
          onChange={setTransposition}
          value={transposition}
        />
      </div>
      <SquareOptions
        fill={fill}
        fillHint={strings.fillHint}
        fillLabel={strings.fill}
        fills={[
          { value: "rows", label: strings.fillRows },
          { value: "columns", label: strings.fillColumns },
          { value: "spiral", label: strings.fillSpiral },
        ]}
        idPrefix="ad"
        kind={kind}
        kindLabel={strings.kind}
        kinds={[
          { value: "merge-ij", label: strings.adfgxMerge },
          { value: "drop-q", label: strings.adfgxDropQ },
          { value: "alnum", label: strings.adfgvx },
        ]}
        onFillChange={setFill}
        onKindChange={setKind}
      />
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const notices = (
    <>
      {usable ? null : <Notice>{strings.shortKey}</Notice>}
      {result.droppedQ > 0 ? (
        <Notice>
          {tPlural(strings.droppedQ, locale, result.droppedQ, {
            count: fmt(result.droppedQ),
          })}
        </Notice>
      ) : null}
      {result.invalid > 0 ? (
        <Notice>
          {tPlural(strings.invalid, locale, result.invalid, {
            count: fmt(result.invalid),
            labels: result.labels,
          })}
        </Notice>
      ) : null}
      {result.unpaired ? (
        <ErrorNotice>
          {t(strings.unpaired, { letter: result.unpaired })}
        </ErrorNotice>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="ad"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        <div className="space-y-1">
          <p className={HINT}>{strings.letterNote}</p>
          {kind === "merge-ij" ? (
            <p className={HINT}>{strings.ijNote}</p>
          ) : null}
        </div>
      }
      working={
        usable ? (
          <Working encoding={encoding} result={result} strings={strings} />
        ) : null
      }
    />
  );
}
